import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { ActiveErrorsActions } from './active-errors.actions';
import { ActiveErrorData } from './active-errors.model';
import { ActiveErrorsState } from './active-errors.state';

export const initActiveErrorsState: ActiveErrorsState = {
  activeErrors: [],
  loading: false,
  loaded: false,
  error: null,
  showAllSideNavActiveErrors: false,
};

export const activeErrorsReducer: ActionReducer<ActiveErrorsState, Action> = createReducer(
  initActiveErrorsState,

  on(
    ActiveErrorsActions.readActiveErrors.attempt,
    (): ActiveErrorsState => ({
      ...initActiveErrorsState,
      activeErrors: [],
      loading: true,
      loaded: false,
    })
  ),

  on(
    ActiveErrorsActions.readActiveErrors.success,
    (state, { activeErrors }): ActiveErrorsState => ({
      ...state,
      activeErrors,
      loading: false,
      loaded: true,
    })
  ),

  on(
    ActiveErrorsActions.readActiveErrors.error,
    (state, { error }): ActiveErrorsState => ({
      ...state,
      activeErrors: [],
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    ActiveErrorsActions.liveUpdateActiveError.new,
    (state, { errorData }): ActiveErrorsState => {
      return {
        ...state,
        activeErrors: [...state.activeErrors, errorData],
      };
    }
  ),

  on(
    ActiveErrorsActions.liveUpdateActiveError.delete,
    (state, { smartNote }): ActiveErrorsState => {
      const newActiveErrors = [...state.activeErrors].filter((entry) => entry.id !== smartNote.id);

      return {
        ...state,
        activeErrors: newActiveErrors,
      };
    }
  ),

  on(
    ActiveErrorsActions.readActiveErrorDetails.attempt,
    (state, { deviceSerial, errorCode }): ActiveErrorsState => ({
      ...state,
      activeErrors: state.activeErrors.map(
        (error): ActiveErrorData => {
          if (deviceSerial === error.deviceSerial && errorCode === error.errorCode) {
            return {
              ...error,
              loading: true,
              loaded: false,
            };
          }

          return error;
        }
      ),
    })
  ),

  on(
    ActiveErrorsActions.readActiveErrorDetails.success,
    (state, { deviceSerial, errorCode, errorDetails, langCode }): ActiveErrorsState => {
      const matchedActiveErrorDetails =
        errorDetails.languageCode.toLocaleLowerCase() === langCode.toLocaleLowerCase()
          ? errorDetails.faultCodes.find(
              (code) => code.faultCode.toLocaleLowerCase() === errorCode.toLocaleLowerCase()
            )
          : undefined;

      return {
        ...state,
        activeErrors: state.activeErrors.map(
          (error): ActiveErrorData => {
            if (deviceSerial === error.deviceSerial && errorCode === error.errorCode) {
              return {
                ...error,
                details: matchedActiveErrorDetails,
                loading: false,
                loaded: true,
              };
            }

            return error;
          }
        ),
      };
    }
  ),

  on(
    ActiveErrorsActions.readActiveErrorDetails.error,
    ActiveErrorsActions.readActiveErrorDetails.errorAborted,
    (state, { deviceSerial, errorCode }): ActiveErrorsState => ({
      ...state,
      activeErrors: state.activeErrors.map(
        (error): ActiveErrorData => {
          if (deviceSerial === error.deviceSerial && errorCode === error.errorCode) {
            return {
              ...error,
              loading: false,
              loaded: true,
            };
          }

          return error;
        }
      ),
    })
  ),

  on(
    ActiveErrorsActions.selectActiveError,
    (state, { gatewaySerial, activeErrorId, isLockedSystem }): ActiveErrorsState => {
      if (isLockedSystem) {
        return {
          ...state,
          activeErrors: state.activeErrors.map(
            (activeError: ActiveErrorData, index: number): ActiveErrorData => {
              if (index === 0) {
                return {
                  ...activeError,
                  isSelected: true,
                };
              }

              return {
                ...activeError,
                isSelected: false,
              };
            }
          ),
        };
      }

      return {
        ...state,
        activeErrors: state.activeErrors.map(
          (activeError): ActiveErrorData => ({
            ...activeError,
            isSelected:
              activeError.id === activeErrorId && activeError.gatewaySerial === gatewaySerial,
          })
        ),
      };
    }
  ),

  on(
    ActiveErrorsActions.selectFirstActiveErrorForDevice,
    (state, { gatewaySerial, deviceSerial }): ActiveErrorsState => {
      let firstSelected = false;

      return {
        ...state,
        activeErrors: state.activeErrors.map(
          (activeError): ActiveErrorData => {
            if (
              activeError.deviceSerial === deviceSerial &&
              activeError.gatewaySerial === gatewaySerial
            ) {
              const newError: ActiveErrorData = {
                ...activeError,
                isSelected: firstSelected ? false : true,
              };

              firstSelected = true;

              return newError;
            }

            return {
              ...activeError,
              isSelected: false,
            };
          }
        ),
      };
    }
  ),
  on(
    ActiveErrorsActions.changeVisibilityOfErrorsCards,
    (state): ActiveErrorsState => {
      return {
        ...state,
        showAllSideNavActiveErrors: !state.showAllSideNavActiveErrors,
      };
    }
  )
);
